const isBrowser = typeof window !== "undefined";
const channel = process.env.REACT_APP_DEFAULT_CHANNEL;

const WEBVIEW = `${channel}-marketplace`;
const FUNCIONALIDADES = "marketplace";
const DEFAULT_CURRENCY = "BRL";
const DEFAULT_AFFILIATION = "livelo-resgate";
const AFFILIATION_DISPLAY = "livelo-resgate";

export const MARKETPLACE_TAG_IDS = Object.freeze({
  // Não-ecommerce
  click: `${channel}.marketplace.01`,
  clickFilter: `${channel}.marketplace.04`,
  clickFilterClose: `${channel}.marketplace.05`,
  clickList: `${channel}.marketplace.03`,
  clickMyAccount: `${channel}.marketplace.06`,
  clickAddressForm: `${channel}.marketplace.07`,
  clickCardForm: `${channel}.marketplace.08`,
  callback: `${channel}.marketplace.09`,
  clickMyRequests: `${channel}.marketplace.10`,
  clickPolicy: `${channel}.marketplace.11`,
  clickSearch: `${channel}.marketplace.12`,
  search: `${channel}.marketplace.13`,
  clickBuy: `${channel}.marketplace.14`,
  clickOrderResume: `${channel}.marketplace.15`,
  clickCheckoutAddresses: `${channel}.marketplace.16`,
  clickResumoCompra: `${channel}.marketplace.17`,
  clickOrderStatus: `${channel}.marketplace.18`,
  login: `${channel}.marketplace.19`,

  // Ecommerce
  banner_impression: `${channel}.marketplace.ecommerce.1`,
  banner_click: `${channel}.marketplace.ecommerce.2`,
  view_item_list: `${channel}.marketplace.ecommerce.3`,
  add_to_wishlist: `${channel}.marketplace.ecommerce.4`,
  select_item: `${channel}.marketplace.ecommerce.5`,
  view_item: `${channel}.marketplace.ecommerce.6`,
  add_to_cart: `${channel}.marketplace.ecommerce.7`,
  view_cart: `${channel}.marketplace.ecommerce.8`,
  remove_from_cart: `${channel}.marketplace.ecommerce.9`,
  begin_checkout: `${channel}.marketplace.ecommerce.10`,
  add_shipping_info: `${channel}.marketplace.ecommerce.11`,
  add_payment_info: `${channel}.marketplace.ecommerce.12`,
  purchase: `${channel}.marketplace.ecommerce.13`,
});

const withDefaults = (value, fallback) =>
  value === undefined || value === null || value === "" ? fallback : value;

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

export const normalizeForTracking = (str) => {
  if (str == null || typeof str !== "string") return "";
  return str
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/gi, "")
    .toLowerCase()
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "";
};

const marketplaceBase = (tagId) => ({
  id_da_tag: tagId,
  webview: WEBVIEW,
});

/** Um item no formato ecommerce.items[] a partir de objeto do carrinho */
const toEcommerceItemFromCartItem = (item, index, coupon = "") => {
  const priceNum = Number(
    item.price?.BRL?.offer ?? item.price?.BRL?.price ?? item.price ?? 0
  );
  const quantity = Number(item.quantity) || 1;
  return {
    affiliation: AFFILIATION_DISPLAY,
    item_id: String(item.uuid ?? item.sku ?? item.id ?? ""),
    item_name: String(item.short_description ?? item.name ?? item.label ?? "").trim(),
    coupon: coupon || undefined,
    discount: Number(normalizeNumber(item.discount)) || 0,
    index,
    item_brand: String(item.seller ?? item.brand ?? "").trim(),
    item_category: String(item.category ?? item.category_label ?? "").trim(),
    price: priceNum,
    quantity,
  };
};

const toEcommerceItemFromParams = ({
  itemId,
  itemName,
  discount = 0,
  index = 0,
  itemBrand = "",
  itemCategory = "",
  price,
  quantity = 1,
  coupon = "",
} = {}) => {
  const priceNum = Number(price) ?? 0;
  const qty = Number(quantity) || 1;
  return {
    affiliation: AFFILIATION_DISPLAY,
    item_id: String(itemId ?? ""),
    item_name: String(itemName ?? "").trim(),
    coupon: coupon || undefined,
    discount: Number(discount) || 0,
    index,
    item_brand: String(itemBrand ?? "").trim(),
    item_category: String(itemCategory ?? "").trim(),
    price: priceNum,
    quantity: qty,
  };
};

const marketplaceBaseWithScreen = (tagId, screenName) => ({
  ...marketplaceBase(tagId),
  Nome_Da_Tela: `marketplace-${screenName}`,
  funcionalidades: FUNCIONALIDADES,
});

const pushEcommerceWithItems = (eventName, tagId, ecommerce) => {
  if (!isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...marketplaceBase(tagId),
    ecommerce,
  });
};

const toEcommerceListItem = ({
  itemId,
  itemName,
  itemListName = "",
  discount = 0,
  index = 0,
  itemBrand = "",
  itemCategory = "",
  price = 0,
  quantity = 1,
  coupon = "",
} = {}) => ({
  affiliation: AFFILIATION_DISPLAY,
  item_id: String(itemId ?? ""),
  item_name: String(itemName ?? "").trim(),
  item_list_name: String(itemListName ?? "").trim(),
  coupon: coupon || undefined,
  discount: Number(normalizeNumber(discount)) || 0,
  index,
  item_brand: String(itemBrand ?? "").trim(),
  item_category: String(itemCategory ?? "").trim(),
  price: Number(price) ?? 0,
  quantity: Number(quantity) || 1,
});

export const sendGA4Event = (eventName, eventParams = {}) => {
  if (!isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  });
  if (process.env.NODE_ENV === "development") {
    console.log("[GTM][GA4]", eventName, eventParams);
  }
};

export const sendGA4EcommerceEvent = (eventName, ecommerce = {}, eventParams = {}) => {
  if (!isBrowser) return;

  window.dataLayer = window.dataLayer || [];
  sendGA4Event(eventName, {
    ecommerce,
    ...eventParams,
  });
};

export const getSearchScreenNameFromPath = (pathname) => {
  if (!pathname || typeof pathname !== "string") return "home";
  const path = pathname.replace(/^\/+/, "").toLowerCase();
  if (path === "loja" || path === "loja/") return "home";
  if (path.startsWith("loja/categories")) return "lista-categorias";
  if (path.startsWith("loja/brands/") && path.split("/").length > 3) return "vitrine-marcas";
  if (path.startsWith("loja/brands")) return "lista-marcas";
  if (path.startsWith("loja/category/")) return "vitrine-categorias";
  if (path.startsWith("loja/myaccount")) return "minha-conta";
  if (path.startsWith("loja/myrequests")) return "meus-pedidos";
  if (path.startsWith("loja/favorites")) return "favoritos";
  if (path.startsWith("loja/policy")) return "termos-e-condicoes";
  if (path.startsWith("loja/products/")) return "resultado-da-busca";
  if (path.startsWith("loja/search-hotels-result")) return "resultado-da-busca";
  if (path.startsWith("loja/search-cars-result")) return "resultado-da-busca";
  if (path.startsWith("loja/searchresulttravel")) return "resultado-da-busca";
  return "home";
};

export const trackClick = ({
  title,
  contentType,
  screenName,
  tagId,
  ...eventParams
}) => {
  const selectedTagId = tagId || MARKETPLACE_TAG_IDS.click;
  sendGA4Event("select_content", {
    elemento: "botao",
    custom_title: title,
    content_type: contentType,
    ...eventParams,
    ...marketplaceBaseWithScreen(selectedTagId, screenName),
  });
};

export const trackCartEvent = ({ title, screenName = "carrinho" }) => {
  trackClick({
    title,
    contentType: "checkout",
    screenName,
    tagId: MARKETPLACE_TAG_IDS.clickOrderResume,
  });
};

export const trackCheckoutAddressEvent = ({ title }) => {
  trackClick({
    title,
    contentType: "checkout",
    screenName: "checkout-enderecos",
    tagId: MARKETPLACE_TAG_IDS.clickCheckoutAddresses,
  });
};

export const trackResumoCompraEvent = ({ title }) => {
  trackClick({
    title,
    contentType: "checkout",
    screenName: "resumo-da-compra",
    tagId: MARKETPLACE_TAG_IDS.clickResumoCompra,
  });
};

export const trackOrderStatusEvent = ({ title, contentType, screenName }) => {
  trackClick({
    title,
    contentType,
    screenName,
    tagId: MARKETPLACE_TAG_IDS.clickOrderStatus,
  });
};

export const trackSearchEvent = ({ title, screenName }) => {
  trackClick({
    title,
    contentType: "busca",
    screenName: screenName || "home",
    tagId: MARKETPLACE_TAG_IDS.clickSearch,
  });
};

export const trackClickBuyEvent = ({ title, screenName }) => {
  trackClick({
    title,
    contentType: "busca",
    screenName: screenName || "home",
    tagId: MARKETPLACE_TAG_IDS.clickBuy,
  });
};

export const trackSearch = (searchTerm, eventParams = {}) => {
  sendGA4Event("search", {
    search_term: searchTerm,
    event_category: "marketplace",
    event_label: "search",
    ...eventParams,
  });
};

export const trackViewItemList = ({
  itemListId,
  itemListName,
  items = [],
  currency = "BRL",
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "view_item_list",
    {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items,
    },
    {
      currency,
      event_category: "marketplace",
      event_label: "view_item_list",
      ...eventParams,
    }
  );
};

export const trackSelectItem = ({
  itemListId,
  itemListName,
  items = [],
  currency = "BRL",
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "select_item",
    {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items,
    },
    {
      currency,
      event_category: "marketplace",
      event_label: "select_item",
      ...eventParams,
    }
  );
};

export const trackViewItem = ({ items = [], currency = "BRL", ...eventParams }) => {
  sendGA4EcommerceEvent(
    "view_item",
    { items },
    {
      currency,
      event_category: "marketplace",
      event_label: "view_item",
      ...eventParams,
    }
  );
};

export const trackAddToCart = ({
  items = [],
  currency = "BRL",
  value,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "add_to_cart",
    { items, value, currency },
    {
      currency,
      value,
      event_category: "marketplace",
      event_label: "add_to_cart",
      ...eventParams,
    }
  );
};

export const trackRemoveFromCart = ({
  items = [],
  currency = "BRL",
  value,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "remove_from_cart",
    { items, value, currency },
    {
      currency,
      value,
      event_category: "marketplace",
      event_label: "remove_from_cart",
      ...eventParams,
    }
  );
};

export const trackViewCart = ({ items = [], currency = "BRL", value, ...eventParams }) => {
  sendGA4EcommerceEvent(
    "view_cart",
    { items, value, currency },
    {
      currency,
      value,
      event_category: "marketplace",
      event_label: "view_cart",
      ...eventParams,
    }
  );
};

export const trackBeginCheckout = ({
  items = [],
  currency = "BRL",
  value,
  coupon,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "begin_checkout",
    { items, value, currency, coupon },
    {
      currency,
      value,
      coupon,
      event_category: "marketplace",
      event_label: "begin_checkout",
      ...eventParams,
    }
  );
};

export const trackAddShippingInfo = ({
  items = [],
  currency = "BRL",
  value,
  shippingTier,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "add_shipping_info",
    { items, value, currency, shipping_tier: shippingTier },
    {
      currency,
      value,
      shipping_tier: shippingTier,
      event_category: "marketplace",
      event_label: "add_shipping_info",
      ...eventParams,
    }
  );
};

export const trackAddPaymentInfo = ({
  items = [],
  currency = "BRL",
  value,
  paymentType,
  coupon,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "add_payment_info",
    { items, value, currency, payment_type: paymentType, coupon },
    {
      currency,
      value,
      payment_type: paymentType,
      coupon,
      event_category: "marketplace",
      event_label: "add_payment_info",
      ...eventParams,
    }
  );
};

export const trackPurchase = ({
  transactionId,
  value,
  tax,
  shipping,
  currency = "BRL",
  coupon,
  affiliation,
  items = [],
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "purchase",
    {
      transaction_id: transactionId,
      value,
      tax,
      shipping,
      currency,
      coupon,
      affiliation,
      items,
    },
    {
      currency,
      value,
      transaction_id: transactionId,
      event_category: "marketplace",
      event_label: "purchase",
      ...eventParams,
    }
  );
};

export const trackMarketplaceLogin = ({
  method = "autenticação",
  screenName = "home",
} = {}) => {
  sendGA4Event("login", {
    method,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.login, screenName),
  });
};

export const trackCallbackMarketplace = ({
  status = "sucesso",
  contentType,
  screenName = "minha-conta",
} = {}) => {
  sendGA4Event("callback", {
    status,
    content_type: contentType,
    Nome_Da_Tela: screenName,
    funcionalidades: FUNCIONALIDADES,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.callback),
  });
};

export const trackMarketplaceSearchResults = ({
  status,
  searchTerm,
  contentType = "busca",
  screenName = "resultado-da-busca",
} = {}) => {
  sendGA4Event("search", {
    status,
    search_term: searchTerm,
    content_type: contentType,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.search, screenName),
  });
};

export const trackMarketplaceBannerImpression = ({
  index,
  custom_title,
  content_type,
  screenName = "home",
  data_inicio,
  data_termino,
  url_imagem,
} = {}) => {
  sendGA4Event("banner_impression", {
    elemento: "banner-carrossel",
    index,
    custom_title,
    content_type,
    data_inicio,
    data_termino,
    url_imagem,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.banner_impression, screenName),
  });
};
    
export const trackMarketplaceBannerClick = ({
  index,
  custom_title,
  content_type,
  screenName = "home",
  data_inicio,
  data_termino,
  url_imagem,
  url_link,
} = {}) => {
  const params = {
    elemento: "banner-carrossel",
    index,
    custom_title: custom_title ?? "",
    content_type: content_type ?? "home",
    data_inicio: data_inicio ?? "",
    data_termino: data_termino ?? "",
    url_imagem: url_imagem ?? "",
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.banner_click, screenName),
  };

  if (url_link) {
    params.url_link = url_link;
  }

  sendGA4Event("banner_click", params);
};

export const trackViewItemListMarketplace = ({
  itemListName = "",
  items = [],
  coupon = "",
} = {}) => {
  if (!isBrowser || !items.length) return;
  const ecommerceItems = items.map((it, i) =>
    toEcommerceListItem({
      ...it,
      itemListName,
      index: it.index !== undefined ? it.index : i,
      coupon,
    })
  );
  const value = ecommerceItems.reduce(
    (sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0
  );
  pushEcommerceWithItems("view_item_list", MARKETPLACE_TAG_IDS.view_item_list, {
    currency: DEFAULT_CURRENCY,
    value,
    items: ecommerceItems,
  });
};

export const trackViewItemListItem = (params = {}) => {
  trackViewItemListMarketplace({
    itemListName: params.itemListName ?? "",
    items: [
      {
        itemId: params.itemId,
        itemName: params.itemName,
        discount: params.discount,
        index: params.index ?? 0,
        itemBrand: params.itemBrand,
        itemCategory: params.itemCategory,
        price: params.price,
        quantity: 1,
      },
    ],
    coupon: params.coupon ?? "",
  });
};

export const trackMarketplaceViewItemListItem = trackViewItemListItem;

export const trackAddToWishlistMarketplace = (params = {}) => {
  if (!isBrowser) return;
  const item = toEcommerceListItem({
    itemListName: params.itemListName ?? "",
    itemId: params.itemId,
    itemName: params.itemName,
    discount: params.discount,
    index: params.index ?? 0,
    itemBrand: params.itemBrand,
    itemCategory: params.itemCategory,
    price: params.price,
    quantity: params.quantity ?? 1,
    coupon: params.coupon ?? "",
  });
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  pushEcommerceWithItems("add_to_wishlist", MARKETPLACE_TAG_IDS.add_to_wishlist, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackMarketplaceAddToWishlist = trackAddToWishlistMarketplace;

export const trackSelectItemMarketplace = (params = {}) => {
  if (!isBrowser) return;
  const item = toEcommerceListItem({
    itemListName: params.itemListName ?? "",
    itemId: params.itemId,
    itemName: params.itemName,
    discount: params.discount,
    index: params.index ?? 0,
    itemBrand: params.itemBrand,
    itemCategory: params.itemCategory,
    price: params.price,
    quantity: params.quantity ?? 1,
    coupon: params.coupon ?? "",
  });
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  pushEcommerceWithItems("select_item", MARKETPLACE_TAG_IDS.select_item, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackMarketplaceSelectItem = trackSelectItemMarketplace;

export const trackViewItemMarketplace = (params = {}) => {
  if (!isBrowser) return;
  const item = toEcommerceListItem({
    itemListName: params.itemListName ?? "detalhe-produto",
    itemId: params.itemId,
    itemName: params.itemName,
    discount: params.discount,
    index: params.index ?? 0,
    itemBrand: params.itemBrand,
    itemCategory: params.itemCategory,
    price: params.price,
    quantity: params.quantity ?? 1,
    coupon: params.coupon ?? "",
  });
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  pushEcommerceWithItems("view_item", MARKETPLACE_TAG_IDS.view_item, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackMarketplaceViewItem = trackViewItemMarketplace;

export const trackMarketplaceAddToCart = ({
  value: valueParam,
  itemId,
  itemName,
  discount = 0,
  index = 0,
  itemBrand = "",
  itemCategory = "",
  price,
  quantity = 1,
  coupon = "",
} = {}) => {
  if (!isBrowser) return;
  const priceNum = Number(price) ?? 0;
  const qty = Number(quantity) || 1;
  const value = valueParam !== undefined && valueParam !== null ? Number(valueParam) : priceNum * qty;
  const item = toEcommerceItemFromParams({
    itemId,
    itemName,
    discount,
    index,
    itemBrand,
    itemCategory,
    price: priceNum,
    quantity: qty,
    coupon,
  });
  pushEcommerceWithItems("add_to_cart", MARKETPLACE_TAG_IDS.add_to_cart, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackAddToCartFromProduct = trackMarketplaceAddToCart;

export const trackViewCartMarketplaceItems = (items = [], options = {}) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { coupon = "" } = options;
  const ecommerceItems = items.map((item, i) => toEcommerceItemFromCartItem(item, i, coupon));
  const value = ecommerceItems.reduce(
    (sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0
  );
  pushEcommerceWithItems("view_cart", MARKETPLACE_TAG_IDS.view_cart, {
    currency: DEFAULT_CURRENCY,
    value,
    items: ecommerceItems,
  });
};

export const trackMarketplaceRemoveFromCart = ({
  itemId,
  itemName,
  discount = 0,
  index = 0,
  itemBrand = "",
  itemCategory = "",
  price,
  quantity = 1,
  value: itemValue,
  coupon = "",
} = {}) => {
  if (!isBrowser) return;
  const priceNum = Number(price) ?? 0;
  const qty = Number(quantity) || 1;
  const value = itemValue !== undefined && itemValue !== null ? Number(itemValue) : priceNum * qty;
  const item = toEcommerceItemFromParams({
    itemId,
    itemName,
    discount,
    index,
    itemBrand,
    itemCategory,
    price: priceNum,
    quantity: qty,
    coupon,
  });
  pushEcommerceWithItems("remove_from_cart", MARKETPLACE_TAG_IDS.remove_from_cart, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackMarketplaceBeginCheckoutItem = ({
  value,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
} = {}) => {
  sendGA4Event("begin_checkout", {
    currency,
    value: normalizeNumber(value),
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: 1,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.begin_checkout),
  });
};

export const trackMarketplaceAddShippingInfoItem = ({
  value,
  shippingTier,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
} = {}) => {
  sendGA4Event("add_shipping_info", {
    currency,
    value: normalizeNumber(value),
    shipping_tier: shippingTier,
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: 1,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.add_shipping_info),
  });
};

export const trackMarketplaceAddPaymentInfoItem = ({
  value,
  paymentType,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
  quantity = 1,
} = {}) => {
  sendGA4Event("add_payment_info", {
    currency,
    value: normalizeNumber(value),
    payment_type: paymentType,
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: Number(quantity) || 1,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.add_payment_info),
  });
};

export const trackMarketplacePurchaseItem = ({
  value,
  transactionId,
  shipping,
  coupon,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
} = {}) => {
  sendGA4Event("purchase", {
    currency,
    value: normalizeNumber(value),
    transaction_id: transactionId,
    shipping: normalizeNumber(shipping),
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: 1,
    coupon,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.purchase),
  });
};

export const trackBannerClick = ({
  index,
  custom_title,
  content_type,
  screenName,
  data_inicio,
  data_termino,
  url_imagem,
  url_link,
}) => {
  const params = {
    elemento: "banner-carrossel",
    index: index + 1,
    custom_title: custom_title || `banner-${index + 1}`,
    content_type: content_type || "",
    data_inicio: data_inicio ?? "",
    data_termino: data_termino ?? "",
    url_imagem: url_imagem ?? "",
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.banner_click, screenName),
  };
  if (url_link) params.url_link = url_link;
  sendGA4Event("banner_click", params);
};

const mapCartItemToTrackingParams = (item, index) => {
  const priceNum = Number(
    item.price?.BRL?.offer ?? item.price?.BRL?.price ?? item.price ?? 0
  );
  const quantity = Number(item.quantity) || 1;
  return {
    value: priceNum * quantity,
    quantity,
    itemId: String(item.uuid ?? item.sku ?? item.id ?? ""),
    itemName: normalizeForTracking(
      item.short_description ?? item.name ?? item.label ?? ""
    ),
    affiliation: item.affiliation ?? DEFAULT_AFFILIATION,
    discount: normalizeNumber(item.discount),
    index: index + 1,
    itemBrand: normalizeForTracking(item.seller ?? item.brand ?? ""),
    itemCategory: normalizeForTracking(item.category ?? item.category_label ?? ""),
    price: priceNum,
    currency: DEFAULT_CURRENCY,
  };
};

const mapCartItemToEcommerceItem = (item, index, coupon = "") => {
  const priceNum = Number(
    item.price?.BRL?.offer ?? item.price?.BRL?.price ?? item.price ?? 0
  );
  const quantity = Number(item.quantity) || 1;
  return {
    affiliation: AFFILIATION_DISPLAY,
    item_id: String(item.uuid ?? item.sku ?? item.id ?? ""),
    item_name: String(item.short_description ?? item.name ?? item.label ?? "").trim(),
    coupon: coupon || undefined,
    discount: Number(normalizeNumber(item.discount)) || 0,
    index,
    item_brand: String(item.seller ?? item.brand ?? "").trim(),
    item_category: String(item.category ?? item.category_label ?? "").trim(),
    price: priceNum,
    quantity,
  };
};

const buildEcommerceItemsAndValue = (items = [], coupon = "") => {
  const ecommerceItems = items.map((item, i) => mapCartItemToEcommerceItem(item, i, coupon));
  const value = ecommerceItems.reduce(
    (sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0
  );
  return { items: ecommerceItems, value };
};

export const trackBeginCheckoutMarketplaceItems = (
  items = [],
  options = {}
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { coupon = "", value: orderValue } = options;
  const { items: ecommerceItems, value: computedValue } = buildEcommerceItemsAndValue(items, coupon);
  const value = orderValue !== undefined && orderValue !== null
    ? Number(orderValue)
    : computedValue;

  const ecommerce = {
    currency: DEFAULT_CURRENCY,
    value,
    coupon: coupon || undefined,
    items: ecommerceItems,
  };

  window.dataLayer = window.dataLayer || [];
  sendGA4Event("begin_checkout", {
    ecommerce,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.begin_checkout),
  });
};

export const trackAddShippingInfoMarketplaceItems = (
  items = [],
  shippingByBrand = {},
  options = {}
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { coupon = "", value: orderValue } = options;
  const { items: ecommerceItems, value: computedValue } = buildEcommerceItemsAndValue(items, coupon);
  const value = orderValue !== undefined && orderValue !== null
    ? Number(orderValue)
    : computedValue;

  const firstBrandId = items[0]?.brand_uuid;
  const brandShipping = shippingByBrand[firstBrandId] || {};
  const days = brandShipping.shipping_deadline ?? brandShipping.estimatedBusinessDays ?? 0;
  const shipping_tier = `frete-${days}-dias-uteis`;

  const ecommerce = {
    currency: DEFAULT_CURRENCY,
    value,
    coupon: coupon || undefined,
    shipping_tier,
    items: ecommerceItems,
  };

  window.dataLayer = window.dataLayer || [];
  sendGA4Event("add_shipping_info", {
    ecommerce,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.add_shipping_info),
  });
};

export const trackAddPaymentInfoMarketplaceItems = (
  items = [],
  paymentType = "cartao-de-credito",
  options = {}
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { value: orderValue, coupon = "" } = options;
  const payment_type =
    paymentType === "pontos" || paymentType === "puntos"
      ? "Points"
      : "Credit Card";

  const { items: ecommerceItems, value } = buildEcommerceItemsAndValue(items, coupon);
  const finalValue =
    orderValue !== undefined && orderValue !== null
      ? Number(orderValue)
      : value;

  const ecommerce = {
    currency: DEFAULT_CURRENCY,
    value: finalValue,
    coupon: coupon || undefined,
    payment_type,
    items: ecommerceItems,
  };

  window.dataLayer = window.dataLayer || [];
  sendGA4Event("add_payment_info", {
    ecommerce,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.add_payment_info),
  });
};

export const trackPurchaseMarketplaceItems = (
  items = [],
  { transactionId, value: orderValue, shipping = 0, coupon = "" } = {}
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0 || !transactionId) return;
  const { items: ecommerceItems, value: computedValue } = buildEcommerceItemsAndValue(items, coupon);
  const value = orderValue !== undefined && orderValue !== null ? Number(orderValue) : computedValue;
  const shippingNum = Number(shipping) ?? 0;

  const ecommerce = {
    transaction_id: transactionId,
    currency: DEFAULT_CURRENCY,
    value,
    shipping: shippingNum,
    coupon: coupon || undefined,
    items: ecommerceItems,
  };

  window.dataLayer = window.dataLayer || [];
  sendGA4Event("purchase", {
    ecommerce,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.purchase),
  });
};

export const trackLoginEvent = ({ method, screenName = "home" }) => {
  sendGA4Event("select_content", {
    elemento: "botao",
    method: method,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.login, screenName),
  });
};
